import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { extractPatientInfo, extractAppointmentTime, extractPEFindings, REASON_KEYWORD_MAP } from './noteUtils';
import { calculateAge } from './useMediscribe';
import { REASON_FOR_VISIT_OPTIONS } from '../constants/mediscribe';

const API = `${window.__WR_API_BASE__}/api`;

/**
 * Hook for all auto-detection and auto-fill logic:
 * - Reason for visit from notes
 * - Patient info extraction (age, gender, status, chief complaint)
 * - Appointment time extraction
 * - Age calculation from DOB
 * - Patient profile auto-fill on role switch
 */
const useAutoDetect = ({
  notes, effectiveRole, user,
  // Reason for visit
  reasonForVisit, setReasonForVisit,
  customReasonForVisit, setCustomReasonForVisit,
  // Patient fields
  soldierRank, setSoldierRank,
  soldierDob,
  patientAge, setPatientAge,
  patientGender, setPatientGender,
  patientStatus, setPatientStatus,
  chiefComplaint, setChiefComplaint,
  autoFilledFields, setAutoFilledFields,
  patientLabel, setPatientLabel,
  // Soldier fields for profile fill
  setSoldierFirstName, setSoldierLastName, setSoldierDob,
  setSoldierUnit, setSoldierPhone,
  setOccupation, setJobDuties,
  // PE findings auto-detection
  includePE, peExceptions, setPeExceptions,
  // Visit type + ICD-10 (for Z71.2 auto-suggest on virtual lab review)
  visitType,
  manualIcd10, setManualIcd10,
}) => {
  const reasonManuallySet = useRef(false);
  const [reasonAutoDetected, setReasonAutoDetected] = useState(false);
  const [timeAutoFilled, setTimeAutoFilled] = useState(false);
  const peFindingsManuallyEdited = useRef(false);

  // Track reason-for-visit usage frequency for smart ordering
  const [reasonUsageCounts, setReasonUsageCounts] = useState(() =>
    JSON.parse(localStorage.getItem('writeright_reason_usage') || '{}')
  );

  const trackReasonUsage = useCallback((value) => {
    if (!value || value === 'other') return;
    setReasonUsageCounts(prev => {
      const updated = { ...prev, [value]: (prev[value] || 0) + 1 };
      localStorage.setItem('writeright_reason_usage', JSON.stringify(updated));
      return updated;
    });
  }, []);

  // Sort reason options: "Other" first, then by usage frequency descending
  const sortedReasonOptions = useMemo(() => {
    const other = REASON_FOR_VISIT_OPTIONS.find(o => o.value === 'other');
    const rest = REASON_FOR_VISIT_OPTIONS.filter(o => o.value !== 'other')
      .sort((a, b) => (reasonUsageCounts[b.value] || 0) - (reasonUsageCounts[a.value] || 0));
    return other ? [other, ...rest] : rest;
  }, [reasonUsageCounts]);

  // Generic feature usage tracking
  const trackFeature = useCallback((feature) => {
    if (!feature) return;
    const guestId = localStorage.getItem('writeright_guest_id');
    axios.post(`${API}/track-feature`, { feature, guest_id: guestId }, { withCredentials: true }).catch(() => {});
  }, []);

  // Auto-detect reason for visit from HPI/notes — only on first significant input
  useEffect(() => {
    if (reasonManuallySet.current || !notes || notes.trim().length < 40) return;
    if (customReasonForVisit && customReasonForVisit.trim().length > 0) return;
    // Only auto-detect once — if reason is still 'other' (untouched)
    if (reasonForVisit !== 'other') return;
    const debounce = setTimeout(() => {
      const text = (' ' + notes.toLowerCase() + ' ');
      for (const { value, keywords } of REASON_KEYWORD_MAP) {
        if (keywords.some(kw => text.includes(kw))) {
          setReasonForVisit(value);
          setCustomReasonForVisit('');
          setReasonAutoDetected(true);
          trackReasonUsage(value);
          return;
        }
      }
    }, 1200);
    return () => clearTimeout(debounce);
  }, [notes]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-calculate age when DOB changes
  useEffect(() => {
    if (soldierDob) {
      const age = calculateAge(soldierDob);
      if (age) setPatientAge(age);
    }
  }, [soldierDob]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-extract patient info from notes — DEBOUNCED 600ms (Feb 27, 2026).
  // Previously this ran synchronously on EVERY keystroke, calling 50+ lines
  // of regex matching against the full notes blob and firing 4-5 setStates.
  // That alone produced visible typing lag on longer encounters. Now it
  // only fires 600ms after the provider pauses typing.
  useEffect(() => {
    if (notes.length < 10) return;
    const t = setTimeout(() => {
      const info = extractPatientInfo(notes);
      if (info.rank && !soldierRank) setSoldierRank(info.rank);
      // Mar 4, 2026 (v6.9.5) — Voice-first workflow. Show a positive toast
      // when demographics are auto-populated from dictation/voice so the
      // provider gets immediate feedback ("we picked up 25 F, you're good
      // to generate"). Aggregated into a single toast so we don't spam.
      const autoFilled = [];
      if (info.age && !patientAge) {
        setPatientAge(info.age);
        setAutoFilledFields(prev => ({ ...prev, age: true }));
        autoFilled.push(`Age ${info.age}`);
      }
      if (info.gender && !patientGender) {
        setPatientGender(info.gender);
        setAutoFilledFields(prev => ({ ...prev, gender: true }));
        autoFilled.push(info.gender === 'male' ? 'Sex M' : 'Sex F');
      }
      if (info.status && !patientStatus) {
        setPatientStatus(info.status);
        setAutoFilledFields(prev => ({ ...prev, status: true }));
      }
      if (autoFilled.length > 0) {
        try {
          toast.success(`Auto-detected: ${autoFilled.join(' · ')}`, { duration: 3000 });
        } catch (_) { /* toast unavailable */ }
      }
      if (info.chiefComplaint && !chiefComplaint && autoFilledFields.chiefComplaint !== false) {
        setChiefComplaint(info.chiefComplaint);
        setAutoFilledFields(prev => ({ ...prev, chiefComplaint: true }));
      }
    }, 600);
    return () => clearTimeout(t);
  }, [notes, soldierRank, patientAge, patientGender, patientStatus, chiefComplaint]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-fill appointment time from dictated notes
  useEffect(() => {
    if (notes.length < 10 || patientLabel || timeAutoFilled) return;
    const debounce = setTimeout(() => {
      const time = extractAppointmentTime(notes);
      if (time) {
        const d = new Date();
        const shortDate = `${d.getDate()} ${d.toLocaleString('en-US', { month: 'short' })}`;
        setPatientLabel(`${shortDate} ${time.display}`);
        setTimeAutoFilled(true);
        toast.success(`Auto-detected appointment: ${time.display}`, { duration: 3000 });
      }
    }, 1500);
    return () => clearTimeout(debounce);
  }, [notes, patientLabel, timeAutoFilled]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-detect PE findings from dictation → populate the Except/Findings input.
  // Conservative: only fills when the box is empty and the user hasn't edited it.
  // The provider can always override or clear manually.
  useEffect(() => {
    if (!setPeExceptions || !includePE) return;
    if (peFindingsManuallyEdited.current) return;
    if (peExceptions && peExceptions.trim().length > 0) return;
    if (!notes || notes.trim().length < 30) return;
    const debounce = setTimeout(() => {
      const detected = extractPEFindings(notes);
      if (detected && detected !== peExceptions) {
        setPeExceptions(detected);
      }
    }, 1500);
    return () => clearTimeout(debounce);
  }, [notes, includePE]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-fill patient profile when entering patient mode (logged-in users)
  useEffect(() => {
    if (effectiveRole === 'patient' && user) {
      axios.get(`${API}/patient/profile`, { withCredentials: true })
        .then(res => {
          const p = res.data;
          if (p.first_name) setSoldierFirstName(p.first_name);
          if (p.last_name) setSoldierLastName(p.last_name);
          if (p.dob) setSoldierDob(p.dob);
          if (p.rank) setSoldierRank(p.rank);
          if (p.unit) setSoldierUnit(p.unit);
          if (p.occupation) setOccupation(p.occupation);
          if (p.job_duties) setJobDuties(p.job_duties);
          if (p.phone) setSoldierPhone(p.phone);
          if (p.patient_status) setPatientStatus(p.patient_status);
        })
        .catch(() => {});
    }
  }, [effectiveRole, user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Z71.2 auto-suggest for Virtual Lab Review encounters.
  // Triggers when EITHER:
  //  - visitType is `results_review_virtual` (provider explicitly chose it), OR
  //  - notes contain a virtual-lab-review phrase (defensive fallback)
  // Only adds the code if not already present in manualIcd10. The provider can
  // still remove or reorder freely. Persists `_z712_seeded:<draftKey>` in
  // sessionStorage so we don't re-add after manual deletion within the same
  // encounter (cleared by clearAll → resetAutoDetect).
  const z712Seeded = useRef(false);
  useEffect(() => {
    if (!setManualIcd10) return;
    if (z712Seeded.current) return;
    const text = (notes || '').toLowerCase();
    const visitMatch = visitType === 'results_review_virtual';
    const phraseMatch = /\bvirtual\s+(lab|labs?|results?)\s+review\b/.test(text)
      || /\b(lab|labs|results?)\s+review\s+(via\s+)?(virtual|telehealth|video)\b/.test(text)
      || /\b(telehealth|virtual)\s+(visit\s+)?(for\s+)?(lab|labs|results?)\s+review\b/.test(text);
    if (!visitMatch && !phraseMatch) return;
    const current = (manualIcd10 || '').toUpperCase();
    if (/\bZ71\.2\b/.test(current)) {
      z712Seeded.current = true;
      return;
    }
    const next = current.trim()
      ? `${manualIcd10}, Z71.2`
      : 'Z71.2';
    setManualIcd10(next);
    z712Seeded.current = true;
    toast.info('Auto-suggested Z71.2 for virtual lab review', { duration: 3000 });
  }, [notes, visitType, manualIcd10, setManualIcd10]);

  // Methods for manual reason setting (used by UI)
  const setReasonManually = useCallback((value) => {
    reasonManuallySet.current = true;
    setReasonAutoDetected(false);
    setReasonForVisit(value);
    trackReasonUsage(value);
    if (value !== 'other') setCustomReasonForVisit('');
  }, [setReasonForVisit, setCustomReasonForVisit, trackReasonUsage]);

  const setCustomReasonManually = useCallback((value) => {
    reasonManuallySet.current = true;
    setCustomReasonForVisit(value);
  }, [setCustomReasonForVisit]);

  // Reset timeAutoFilled (called by clearAll)
  const resetAutoDetect = useCallback(() => {
    reasonManuallySet.current = true;
    setReasonAutoDetected(false);
    setReasonForVisit('other');
    setCustomReasonForVisit('');
    setTimeAutoFilled(false);
    peFindingsManuallyEdited.current = false;
    z712Seeded.current = false;
    setTimeout(() => { reasonManuallySet.current = false; }, 1500);
  }, [setReasonForVisit, setCustomReasonForVisit]);

  // Called by the PE findings input when the user types — prevents auto-detect
  // from overwriting manual edits within the same encounter.
  const markPeFindingsManual = useCallback(() => {
    peFindingsManuallyEdited.current = true;
  }, []);

  return {
    reasonAutoDetected, reasonManuallySet,
    reasonUsageCounts, sortedReasonOptions,
    trackReasonUsage, trackFeature,
    setReasonManually, setCustomReasonManually,
    resetAutoDetect, timeAutoFilled, setTimeAutoFilled,
    markPeFindingsManual,
  };
};

export default useAutoDetect;
